import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Library, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ScheduleRow {
  airline_code: string;
  schedule_version: string;
  valid_from: string;
  valid_to: string;
  pattern_count: number;
  imported_at: string;
  file_hash: string | null;
}

interface ScheduleLibraryProps {
  refreshKey: number;
}

export default function ScheduleLibrary({ refreshKey }: ScheduleLibraryProps) {
  const [rows, setRows] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ScheduleRow | null>(null);
  const { toast } = useToast();

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("flight_patterns")
      .select("airline_code, schedule_version, valid_from, valid_to, imported_at, file_hash");

    if (error) {
      console.error("Failed to fetch schedules:", error.message);
      setLoading(false);
      return;
    }

    // Group by airline_code + schedule_version
    const map = new Map<string, ScheduleRow>();
    for (const p of data || []) {
      const key = `${p.airline_code}|${p.schedule_version}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, {
          airline_code: p.airline_code,
          schedule_version: p.schedule_version,
          valid_from: p.valid_from,
          valid_to: p.valid_to,
          pattern_count: 1,
          imported_at: p.imported_at || "",
          file_hash: (p as any).file_hash || null,
        });
      } else {
        existing.pattern_count++;
        if (p.valid_from < existing.valid_from) existing.valid_from = p.valid_from;
        if (p.valid_to > existing.valid_to) existing.valid_to = p.valid_to;
        if (p.imported_at && p.imported_at > existing.imported_at) existing.imported_at = p.imported_at;
        if (!existing.file_hash && (p as any).file_hash) existing.file_hash = (p as any).file_hash;
      }
    }

    setRows(Array.from(map.values()).sort((a, b) => b.imported_at.localeCompare(a.imported_at)));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules, refreshKey]);

  const handleDelete = async (row: ScheduleRow) => {
    const key = `${row.airline_code}|${row.schedule_version}`;
    setDeleting(key);

    // First delete cache rows linked to these patterns
    const { data: patternIds } = await supabase
      .from("flight_patterns")
      .select("id")
      .eq("airline_code", row.airline_code)
      .eq("schedule_version", row.schedule_version);

    if (patternIds && patternIds.length > 0) {
      const ids = patternIds.map((p) => p.id);
      await supabase.from("flight_daily_cache").delete().in("pattern_id", ids as any);
    }

    // Delete patterns
    const { error } = await supabase
      .from("flight_patterns")
      .delete()
      .eq("airline_code", row.airline_code)
      .eq("schedule_version", row.schedule_version);

    setDeleting(null);
    setConfirmDelete(null);

    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: `${row.schedule_version} for ${row.airline_code} removed.` });
      fetchSchedules();
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Library className="h-5 w-5" />
            Schedule Library
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No schedules imported yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Airline</TableHead>
                  <TableHead>Schedule Version</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid To</TableHead>
                  <TableHead>Patterns</TableHead>
                  <TableHead>File Hash</TableHead>
                  <TableHead>Imported At</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const key = `${row.airline_code}|${row.schedule_version}`;
                  return (
                    <TableRow key={key}>
                      <TableCell className="font-mono font-medium">{row.airline_code}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.schedule_version}</Badge>
                      </TableCell>
                      <TableCell>{row.valid_from}</TableCell>
                      <TableCell>{row.valid_to}</TableCell>
                      <TableCell>{row.pattern_count}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {row.file_hash ? `${row.file_hash.substring(0, 8)}…` : 'N/A'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.imported_at ? new Date(row.imported_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={deleting === key}
                          onClick={() => setConfirmDelete(row)}
                        >
                          {deleting === key ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <strong>{confirmDelete?.schedule_version}</strong> for{" "}
              <strong>{confirmDelete?.airline_code}</strong>? This will also remove all associated cache rows.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
